import React, { useMemo } from 'react';

const MODE_COLORS = {
  FOCUS: { stroke: '#e53935', glow: 'rgba(229, 57, 53, 0.3)', text: '#e53935' },
  SHORT_BREAK: { stroke: '#43a047', glow: 'rgba(67, 160, 71, 0.3)', text: '#43a047' },
  LONG_BREAK: { stroke: '#1e88e5', glow: 'rgba(30, 136, 229, 0.3)', text: '#1e88e5' },
};

const SIZE = 280;
const STROKE_WIDTH = 10;
const RADIUS = (SIZE - STROKE_WIDTH) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * Animated SVG circular progress ring timer display.
 *
 * Props:
 *  remainingSeconds: number — seconds left
 *  totalSeconds: number    — total duration for this session
 *  mode: 'FOCUS' | 'SHORT_BREAK' | 'LONG_BREAK'
 *  status: 'IDLE' | 'RUNNING' | 'PAUSED' | 'COMPLETED'
 */
const CircularTimer = ({ remainingSeconds, totalSeconds, mode, status }) => {
  const colors = MODE_COLORS[mode] || MODE_COLORS.FOCUS;

  const progress = useMemo(() => {
    if (!totalSeconds || totalSeconds === 0) return 1;
    const clamped = Math.max(0, Math.min(remainingSeconds, totalSeconds));
    return clamped / totalSeconds;
  }, [remainingSeconds, totalSeconds]);

  const dashOffset = CIRCUMFERENCE * (1 - progress);

  const minutes = Math.floor(Math.max(0, remainingSeconds) / 60);
  const seconds = Math.max(0, remainingSeconds) % 60;
  const timeStr = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

  const modeLabel = {
    FOCUS: 'Focus',
    SHORT_BREAK: 'Short Break',
    LONG_BREAK: 'Long Break',
  }[mode] || mode;

  const isPaused = status === 'PAUSED';
  const isIdle = status === 'IDLE';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <svg
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        style={{ filter: `drop-shadow(0 0 20px ${colors.glow})` }}
      >
        {/* Background track */}
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke="rgba(255,255,255,0.06)"
          strokeWidth={STROKE_WIDTH}
        />

        {/* Progress arc */}
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke={colors.stroke}
          strokeWidth={STROKE_WIDTH}
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={dashOffset}
          transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
          style={{
            transition: 'stroke-dashoffset 1s linear, stroke 0.5s ease',
            opacity: isPaused ? 0.5 : 1,
          }}
        />

        {/* Time text */}
        <text
          x={SIZE / 2}
          y={SIZE / 2 - 8}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="white"
          fontSize="48"
          fontWeight="700"
          fontFamily="'Inter', 'Roboto', sans-serif"
          style={{ letterSpacing: '2px' }}
        >
          {timeStr}
        </text>

        {/* Status label */}
        <text
          x={SIZE / 2}
          y={SIZE / 2 + 36}
          textAnchor="middle"
          dominantBaseline="middle"
          fill={isIdle ? 'rgba(255,255,255,0.4)' : colors.text}
          fontSize="14"
          fontWeight="500"
          fontFamily="'Inter', 'Roboto', sans-serif"
        >
          {isIdle ? 'Ready' : isPaused ? 'Paused' : modeLabel}
        </text>
      </svg>
    </div>
  );
};

export default CircularTimer;
